import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE = `${process.env.REACT_APP_SERVER_API}/position-tracker`;

const authHeaders = () => {
  const token = localStorage?.getItem("token");
  return {
    Authorization: `Bearer ${token}`,
    Accept: "application/json",
    "Content-Type": "application/json",
  };
};

export const getTrackedProducts = createAsyncThunk(
  "positionTracker/getTrackedProducts",
  async (_, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await axios.get(`${BASE}/products/`, {
        headers: authHeaders(),
      });
      return fulfillWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Unknown error" });
    }
  }
);

export const getTrackedProduct = createAsyncThunk(
  "positionTracker/getTrackedProduct",
  async (id, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await axios.get(`${BASE}/products/${id}/`, {
        headers: authHeaders(),
      });
      return fulfillWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Unknown error" });
    }
  }
);

export const createTrackedProduct = createAsyncThunk(
  "positionTracker/createTrackedProduct",
  async (data, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await axios.post(`${BASE}/products/`, data, {
        headers: authHeaders(),
      });
      return fulfillWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Unknown error" });
    }
  }
);

export const updateTrackedProduct = createAsyncThunk(
  "positionTracker/updateTrackedProduct",
  async ({ id, data }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await axios.patch(`${BASE}/products/${id}/`, data, {
        headers: authHeaders(),
      });
      return fulfillWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Unknown error" });
    }
  }
);

export const deleteTrackedProduct = createAsyncThunk(
  "positionTracker/deleteTrackedProduct",
  async (id, { rejectWithValue, fulfillWithValue }) => {
    try {
      await axios.delete(`${BASE}/products/${id}/`, { headers: authHeaders() });
      return fulfillWithValue(id);
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Unknown error" });
    }
  }
);

export const updateQueryComment = createAsyncThunk(
  "positionTracker/updateQueryComment",
  async (
    { productId, queryId, comment },
    { rejectWithValue, fulfillWithValue }
  ) => {
    try {
      const response = await axios.patch(
        `${BASE}/products/${productId}/queries/${queryId}/`,
        { comment },
        { headers: authHeaders() }
      );
      return fulfillWithValue({ productId, query: response.data });
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Unknown error" });
    }
  }
);

export const startPositionCheck = createAsyncThunk(
  "positionTracker/startPositionCheck",
  async (id, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE}/products/${id}/check/`,
        {},
        { headers: authHeaders() }
      );
      return fulfillWithValue({ id, ...response.data });
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Unknown error" });
    }
  }
);

export const initStatePositionTracker = {
  products: { data: [], loading: false, error: null },
  productDetail: { data: null, loading: false, error: null },
  createProduct: { data: null, loading: false, error: null },
  updateProduct: { loading: false, error: null },
  deleteProduct: { loading: false, error: null },
  startCheck: { loading: false, error: null, data: null },
};

export const reducerPositionTracker = {
  [getTrackedProducts.pending]: (state) => {
    state.products.loading = true;
  },
  [getTrackedProducts.fulfilled]: (state, action) => {
    state.products.loading = false;
    state.products.data = action.payload;
    state.products.error = null;
  },
  [getTrackedProducts.rejected]: (state, action) => {
    state.products.loading = false;
    state.products.error = action.payload;
  },

  [getTrackedProduct.pending]: (state) => {
    state.productDetail.loading = true;
  },
  [getTrackedProduct.fulfilled]: (state, action) => {
    state.productDetail.loading = false;
    state.productDetail.data = action.payload;
    state.productDetail.error = null;
  },
  [getTrackedProduct.rejected]: (state, action) => {
    state.productDetail.loading = false;
    state.productDetail.error = action.payload;
  },

  [createTrackedProduct.pending]: (state) => {
    state.createProduct.loading = true;
  },
  [createTrackedProduct.fulfilled]: (state, action) => {
    state.createProduct.loading = false;
    state.createProduct.data = action.payload;
    state.createProduct.error = null;
    state.products.data = [action.payload, ...state.products.data];
  },
  [createTrackedProduct.rejected]: (state, action) => {
    state.createProduct.loading = false;
    state.createProduct.error = action.payload;
  },

  [updateTrackedProduct.pending]: (state) => {
    state.updateProduct.loading = true;
  },
  [updateTrackedProduct.fulfilled]: (state, action) => {
    state.updateProduct.loading = false;
    state.updateProduct.error = null;
    state.products.data = state.products.data.map((p) =>
      p.id === action.payload.id ? action.payload : p
    );
  },
  [updateTrackedProduct.rejected]: (state, action) => {
    state.updateProduct.loading = false;
    state.updateProduct.error = action.payload;
  },

  [deleteTrackedProduct.pending]: (state) => {
    state.deleteProduct.loading = true;
  },
  [deleteTrackedProduct.fulfilled]: (state, action) => {
    state.deleteProduct.loading = false;
    state.deleteProduct.error = null;
    state.products.data = state.products.data.filter(
      (p) => p.id !== action.payload
    );
  },
  [deleteTrackedProduct.rejected]: (state, action) => {
    state.deleteProduct.loading = false;
    state.deleteProduct.error = action.payload;
  },

  [updateQueryComment.fulfilled]: (state, action) => {
    const { productId, query } = action.payload;
    const product = state.products.data.find((p) => p.id === productId);
    if (product) {
      product.queries = product.queries.map((q) =>
        q.id === query.id ? { ...q, comment: query.comment } : q
      );
    }
  },

  [startPositionCheck.pending]: (state) => {
    state.startCheck.loading = true;
  },
  [startPositionCheck.fulfilled]: (state, action) => {
    state.startCheck.loading = false;
    state.startCheck.data = action.payload;
    state.startCheck.error = null;
  },
  [startPositionCheck.rejected]: (state, action) => {
    state.startCheck.loading = false;
    state.startCheck.error = action.payload;
  },
};
